import React, { FC, useEffect, useMemo, useRef, useState } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';

import { HorizontalScroller } from 'components/horizontal-scroller';
import useMediaQuery from 'hooks/use-media-query';
import { useScrollOverflow } from 'hooks/use-scroll-overflow';

import {
  CardContainer,
  CardContainerWrapper,
  CardWrapper,
  CardsWrapper,
  Container,
  LeftButtonWrapper,
  LeftCarouselButton,
  RightButtonWrapper,
  RightCarouselButton,
  Spacer,
} from './responsive-carousel.styled';
import { IResponsiveCarouselProps, Sizes } from './types';

const MAX_WIDTH = 1050;
const CONTAINER_PADDING = 16;

const ResponsiveCarousel: FC<IResponsiveCarouselProps> = ({
  children,
  bgColor = Styles.color.white,
  scrollbarTheme,
  centerCards = false,
  alignWithParent,
}) => {
  const [page, setPage] = useState(0);
  const cardWrapperRef = useRef<HTMLDivElement>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery('mobile');
  const { isOverflowingLeft, isOverflowingRight, isOverflowing } =
    useScrollOverflow(cardContainerRef);

  const showMore = () => {
    setPage(currentPage => currentPage + 1);
  };

  const showLess = () => {
    setPage(currentPage => currentPage - 1);
  };

  const totalCards = children.length;
  const containerWidth = containerRef?.current?.offsetWidth || MAX_WIDTH;
  // Use ref and spacer to determine individual card widths
  const cardWidth = (cardWrapperRef.current?.clientWidth ?? 0) + Sizes.SPACER;
  // use the container width otherwise 1050px
  const maxWidth = Math.min(containerWidth, MAX_WIDTH);
  const isMaxWidth = window.innerWidth >= maxWidth;
  const cardContainerWidth = isMaxWidth ? maxWidth : window.innerWidth;
  // Number of visible cards is the current card container width divided by card width
  const numberOfVisibleCards = Math.floor((cardContainerWidth - CONTAINER_PADDING) / cardWidth);
  // Total space cards take up, this does not include margin
  const cardSpace = numberOfVisibleCards * cardWidth;
  // Calculate inner margin to center cards
  const innerPadding =
    totalCards > 1 ? Math.round((cardContainerWidth - cardSpace) / 2) : CONTAINER_PADDING;
  // Buttons don't show for mobile
  const showLeftBtn = !isMobile && page !== 0;
  const showRightBtn = !isMobile && (page + 1) * numberOfVisibleCards < totalCards;

  // When the number of cards change, reset current page
  useEffect(() => {
    if (!isMobile) {
      setPage(0);
    }
  }, [numberOfVisibleCards, isMobile]);

  const offset = page * cardSpace;

  const renderChildren = useMemo(
    () =>
      children.map((child, i) => (
        <CardWrapper
          ref={cardWrapperRef}
          key={`single_carousel_card_${i}`}
          data-testid="single-carousel-card"
        >
          {child}
        </CardWrapper>
      )),
    [children]
  );

  return (
    <Container ref={containerRef} isMobile={isMobile}>
      {showLeftBtn && (
        <LeftButtonWrapper isMobile={isMobile} bgColor={bgColor} isOverflow={isOverflowingLeft}>
          <LeftCarouselButton onClick={showLess}>
            <Icon icon="previous" color="icon-reversed" width="16px" aria-hidden />
          </LeftCarouselButton>
        </LeftButtonWrapper>
      )}
      <CardContainerWrapper isMobile={isMobile}>
        <HorizontalScroller hidden={!isMobile} theme={scrollbarTheme}>
          <CardContainer
            key={`${isMobile}`}
            ref={cardContainerRef}
            isMobile={isMobile}
            alignWithParent={alignWithParent}
            $innerPadding={innerPadding}
            $isMaxWidth={isMaxWidth}
            $centerContent={centerCards && !isOverflowing}
          >
            {isMobile ? (
              renderChildren
            ) : (
              <CardsWrapper key={numberOfVisibleCards} $offset={offset}>
                {renderChildren}
              </CardsWrapper>
            )}
            {children?.length > 1 && <Spacer />}
          </CardContainer>
        </HorizontalScroller>
      </CardContainerWrapper>
      {showRightBtn && (
        <RightButtonWrapper isMobile={isMobile} bgColor={bgColor} isOverflow={isOverflowingRight}>
          <RightCarouselButton onClick={showMore}>
            <Icon icon="next" color="icon-reversed" width="16px" aria-hidden />
          </RightCarouselButton>
        </RightButtonWrapper>
      )}
    </Container>
  );
};

export default ResponsiveCarousel;
